import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Paging } from "types/common.types";

import { User } from "store/auth/types";

import {
  deactivateUser,
  getInvitableUsersToNewMeeting,
  getInvitableUsersToTask,
  getInvitableUsersToVoting,
  getProfile,
  getUsers,
  updateProfile,
} from "./actions";

export const REGISTER_SUCCESS = "/auth/register/fulfilled";
export const RESET_PASSSWORD_SUCCESS = "/auth/reset-password/fulfilled";
export const PROFILE_SUCCESS = "/auth/profile/fulfilled";

export type ById<Model> = {
  loading: boolean;
  data: Model;
  error: any;
};

export type UsersState = {
  error: any;
  token: null | string;
  loading: boolean;
  users: User[];
  invitableUsers: User[];
  byId: ById<User> | {};
  paging: Paging;
  pagingInvitableUsers: Paging;
  selectedUserId: number | null;
};

const initialState: UsersState = {
  loading: false,
  token: null,
  error: false,
  users: [],
  invitableUsers: [],
  byId: {},
  paging: {} as Paging,
  pagingInvitableUsers: {} as Paging,
  selectedUserId: null,
};

export const usersSlice = createSlice({
  initialState,
  name: "users",
  reducers: {
    setSelectedUserId: (
      state: UsersState,
      action: PayloadAction<number | null>
    ) => {
      state.selectedUserId = action.payload;
    },
  },
  extraReducers: (builder) => {
    // GET USERS
    builder.addCase(getUsers.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.users = action.payload.data;
      state.paging = action.payload.meta;
    });
    builder.addCase(getUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getUsers.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });

    // UPDATE  PROFILE
    builder.addCase(updateProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(updateProfile.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(updateProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.byId = {
        ...state.byId,
        [action.meta.arg.userId]: {
          data: action.payload.data,
          loading: false,
          error: false,
        },
      };
      state.error = false;
    });
    // GET PROFILE
    builder.addCase(getProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = false;
      state.byId = {
        ...state.byId,
        [action.meta.arg]: {
          loading: true,
          error: action.payload,
        },
      };
    });
    builder.addCase(getProfile.pending, (state, action) => {
      state.loading = true;
      state.error = false;
      state.byId = {
        ...state.byId,
        [action.meta.arg]: {
          loading: true,
          error: false,
        },
      };
    });
    builder.addCase(getProfile.fulfilled, (state, action) => {
      state.loading = false;

      state.byId = {
        ...state.byId,
        [action.meta.arg]: {
          data: action.payload.data,
          loading: false,
          error: false,
        },
      };
      state.error = false;
    });

    // DEACTIVATE USER
    builder.addCase(deactivateUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(deactivateUser.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deactivateUser.fulfilled, (state, action) => {
      state.loading = false;
      // state.data = action.payload.data;
      state.error = false;
    });

    //invitable users for meeting
    builder.addCase(getInvitableUsersToNewMeeting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getInvitableUsersToNewMeeting.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(
      getInvitableUsersToNewMeeting.fulfilled,
      (state, action) => {
        state.loading = false;
        state.error = false;
        state.invitableUsers = action.payload.data;
        state.pagingInvitableUsers = action.payload.meta;
      }
    );

    //invitable users for voting
    builder.addCase(getInvitableUsersToVoting.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getInvitableUsersToVoting.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getInvitableUsersToVoting.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.invitableUsers = action.payload.data;
      state.pagingInvitableUsers = action.payload.meta;
    });

    //invitable users for task
    builder.addCase(getInvitableUsersToTask.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getInvitableUsersToTask.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getInvitableUsersToTask.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.invitableUsers = action.payload.data;
      state.pagingInvitableUsers = action.payload.meta;
    });
  },
});

// Action creators are generated for each case reducer function
export const { setSelectedUserId } = usersSlice.actions;
export default usersSlice.reducer;
